import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	Link,
	makeStyles,
	Menu,
	MenuItem,
	Select,
	Tab,
	Tabs,
	TextField,
} from '@material-ui/core';
import CopyToClipboard from 'react-copy-to-clipboard';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AddIcon from '@material-ui/icons/Add';
import SubdirectoryArrowRightRoundedIcon from '@material-ui/icons/SubdirectoryArrowRightRounded';

import { setInspectMediaModified } from '../../../../actions/inspectScreenActions';
import { publishPortalDetailsAction, savePortalDetailsAction } from '../../../../actions/publishActions';
import { fetchAccountPortalThemesService } from '../../../../services/portalApiService';
import { Theme } from '../../InspectScreen/InspectSections/PortalThemes/hooks/useThemes';
import { loadMediasPerNavigationWithTotalCount } from '../../../../services/mediaService';
import { mediaNames, sortTypes } from '../../../../constants/mediaConstants';
import { MediaDetailUtils } from '../utils';
import { envName, portalsUrl, qportalLoaderUrl } from '../../../../utils/config';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { LocalStorageService } from '../../../../services/localStorageService';
import { _getAccountSettings } from '../../../../services/profileServices';
import {
	CONTENT_TYPE_BANNER,
	CONTENT_TYPE_HERO_VIDEO,
	CONTENT_TYPE_PLAYLIST,
	CONTENT_TYPE_SEARCH_FILTER,
	contentAddMenuItems,
	FOOTER_CALL_TO_ACTION,
	FOOTER_SIMPLE,
	FOOTER_SOCIAL_MEDIA_LINKS,
	footerAddMenuItems,
	PLAYLIST_DISPLAY_TYPE_GRID,
} from '../../../../constants/portalConfigConstant';
import ContentListItem from './portalContent/ContentListItem';
import { ContentItem } from './portalContent/ContentItem';
import { generateUUID } from '../../../../utils/commonUtil';
import FooterListItem from './portalFooter/FooterListItem';

const useStyles = makeStyles((_theme) => ({
    tickSize: {
        transform: "scale(1.2)",
    },
}));

type PortalContentProps = {
	baseClassName: string;
	portalDetail: any;
	miniMediaLibraryRef: any;
	accountPreset: string[];
	handleOnDataChanges: () => void;
	setIsThumbnailChanged: (_isChanged: boolean) => void;
	reset: () => void;
};

const PortalContent = forwardRef(
	(
		{
			baseClassName,
			portalDetail,
			miniMediaLibraryRef,
			accountPreset,
			handleOnDataChanges,
			setIsThumbnailChanged,
			reset,
		}: PortalContentProps,
		ref
	) => {
    	const customStyles = useStyles();
		const { t } = useTranslation();
		const dispatch = useDispatch<any>();
		const { defaultAccountId } = useSelector((state: any) => state.session);

		const [selectedTab, setSelectedTab] = useState<string>('detail');

		const [themes, setThemes] = useState<Theme[]>([]);
		const themesRef = useRef<Theme[]>([]);
		const [playlists, setPlaylists] = useState<any[]>([]);

		const [pageTitle, setPageTitle] = useState<string>('');
		const [selectedThemesId, setSelectedThemesId] = useState<string>('');
		const [shareThumbnail, setShareThumbnail] = useState<string>('');
		const [protectedMedias, setProtectedMedias] = useState<boolean>(false);

		const [isGeneralSectionOpen, setIsGeneralSectionOpen] = useState<boolean>(true);
		const [isContentSectionOpen, setIsContentSectionOpen] = useState<boolean>(true);
		const [isFooterSectionOpen, setIsFooterSectionOpen] = useState<boolean>(true);

		const [contentItems, setContentItems] = useState<any[]>([]);
		const [footerItems, setFooterItems] = useState<any[]>([]);

		const [contentAddMenuAnchorEl, setContentAddMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
		const [footerAddMenuAnchorEl, setFooterAddMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

		useImperativeHandle(ref, () => {
			return { onSaveChanges, onPublishPortal, onSelectedHeroMedia };
		});

		const onSaveChanges = async (callback: any = null) => {
			if (!portalDetail) {
				return;
			}

			const newData = {
				content: {
					...portalDetail.content,
					name: pageTitle,
					shareThumbnail: shareThumbnail,
					protectedMedias: protectedMedias
				},
				themeId: selectedThemesId,
			};

			//remove legacy property when user update portal, could be remove in future
			delete newData.content.playlistId;

			// end legacy property

			if (contentItems.length > 0) {
				const contentBanner = contentItems.find((item) => item.contentType === CONTENT_TYPE_BANNER);

				newData.content.main = {
					...newData.content.main,
					banner: contentBanner
						? {
								imageUrl: contentBanner.imageUrl ?? '',
								enableCustomHeight: !!contentBanner.enableCustomHeight,
								height:
									contentBanner.enableCustomHeight && contentBanner.height
										? Number(contentBanner.height)
										: undefined,
								mobileHeight:
									contentBanner.enableCustomHeight && contentBanner.mobileHeight
										? Number(contentBanner.mobileHeight)
										: undefined,
							}
						: undefined,
				};

				const contentHeroVideo = contentItems.find((item) => item.contentType === CONTENT_TYPE_HERO_VIDEO);

				if (contentHeroVideo) {
					newData.content.main = {
						...newData.content.main,
						heroVideo: {
							...contentHeroVideo,
							type: contentHeroVideo.mediaType,
							title: contentHeroVideo.mediaTitle,
							description: contentHeroVideo.mediaDescription,
							id: contentHeroVideo.mediaId,
							videoRatio: 0.5625,
							hasAttachedForm: contentHeroVideo.hasAttachedForm,
						},
					};
				} else {
					newData.content.main = { ...newData.content.main, heroVideo: undefined };
				}

				// needs to order playlists and search filters against each other before placing them in separate arrays
				const playlists: any = [];
				const searchFilters: any = [];
				contentItems.forEach((item, index) => {
					if (item.contentType === CONTENT_TYPE_PLAYLIST) {
						playlists.push({
							type: item.displayType,
							playlistId: item.playlistId,
							enableHeadingItem: item.isShowTitle,
							headingType: item.showTitleType,
							grid: {
								rows: item.rows,
								columns: 4,
							},
							order: index,
						});
					}
					if (item.contentType === CONTENT_TYPE_SEARCH_FILTER) {
						searchFilters.push({
							type: item.displayType,
							data: item.data,
							enableHeadingItem: item.isShowTitle,
							headingType: item.showTitleType,
							grid: {
								rows: item.rows,
								columns: 4,
							},
							order: index,
						});
					}
				});
				newData.content.main.playlists = playlists;
				newData.content.main.searchFilters = searchFilters;
			} else {
				newData.content.main = {
					...newData.content.main,
					banner: undefined,
					heroVideo: undefined,
					playlists: [],
					searchFilters: [],
				};
			}

			if (footerItems.length > 0) {
				const callToActionItem = footerItems.find((item) => item.footerType === FOOTER_CALL_TO_ACTION);

				if (callToActionItem) {
					newData.content.footer = {
						callToAction: {
							headline: callToActionItem.headline,
							description: callToActionItem.description,
							link: callToActionItem.link,
						},
					};
				} else {
					newData.content.footer = { callToAction: undefined };
				}

				const socialMediaLinksItem = footerItems.find((item) => item.footerType === FOOTER_SOCIAL_MEDIA_LINKS);

				if (socialMediaLinksItem) {
					newData.content.footer = {
						...newData.content.footer,
						socialMediaLinks: socialMediaLinksItem.links.map(({ type, url }: any) => {
							return {
								type,
								url,
							};
						}),
					};
				} else {
					newData.content.footer = {
						...newData.content.footer,
						socialMediaLinks: undefined,
					};
				}

				const simpleFooterItem = footerItems.find((item) => item.footerType === FOOTER_SIMPLE);
				newData.content.footer = {
					...newData.content.footer,
					simpleFooter: simpleFooterItem ? simpleFooterItem.content : undefined,
				};
			} else {
				newData.content.footer = {};
			}

			try {
				await dispatch(savePortalDetailsAction(defaultAccountId, portalDetail.id, newData));
				const previewUrl = `${portalsUrl}${
					portalDetail.id
				}?pu=${LocalStorageService.getAccessToken()}&aid=${defaultAccountId}`;

				callback?.(previewUrl);
			} catch (e) {
				console.error('Error saving portal', e);
			}
		};

		const onPublishPortal = async (callback: any) => {
			if (!portalDetail) {
				return;
			}

			try {
				await onSaveChanges();
				await dispatch(publishPortalDetailsAction(defaultAccountId, portalDetail.id));
				const previewUrl = `${portalsUrl}${portalDetail.code}`;
				callback?.(previewUrl);
			} catch (e) {
				console.error('Error publishing portal', e);
			}
		};

		const handleUpdatingData = useCallback(() => {
			handleOnDataChanges?.();
			dispatch(setInspectMediaModified(true));
		}, [dispatch, handleOnDataChanges]);

		const onSelectedHeroMedia = (media: any) => {
			if (!media || !media?.[0]) {
				return;
			}

			const selectedMedia = media[0];
			const { type, title, id, description, links } = selectedMedia;
			const contentHeroVideo = contentItems.find((item) => item.contentType === CONTENT_TYPE_HERO_VIDEO);
			if (!contentHeroVideo) {
				return;
			}
			const newContentHeroVideo = {
				...contentHeroVideo,
				mediaId: id,
				mediaTitle: title,
				mediaDescription: description,
				mediaType: type,
				hasAttachedForm: links && !!links.find((link: { id: String, type: String }) => link.type === "Form")
			};
			setContentItems((prev) => {
				return prev.map((item) => {
					if (item.contentType === CONTENT_TYPE_HERO_VIDEO) {
						return newContentHeroVideo;
					}
					return item;
				});
			});
			handleUpdatingData();
		};

		const handleClearSelectedHeroMedia = () => {
			setContentItems((prev) => {
				return prev.map((item) => {
					if (item.contentType === CONTENT_TYPE_HERO_VIDEO) {
						return {
							...item,
							mediaId: '',
							mediaTitle: '',
							mediaDescription: '',
							mediaType: '',
							hasAttachedForm: false,
						};
					}
					return item;
				});
			});
			handleUpdatingData();
		};

		const handleUpdateHeroMedia = (key: any, value: any) => {
			setContentItems((prev) => {
				return prev.map((item) => {
					if (item.contentType === CONTENT_TYPE_HERO_VIDEO) {
						return {
							...item,
							[key]: value,
						};
					}
					return item;
				});
			});
			handleUpdatingData();
		};

		const handleUpdateBanner = (key: any, value: any) => {
			setContentItems((prev) => {
				return prev.map((item) => {
					if (item.contentType === CONTENT_TYPE_BANNER) {
						return {
							...item,
							[key]: value,
						};
					}
					return item;
				});
			});
			handleUpdatingData();
		};

		const handleUpdatePlaylist = (contentId: any, key: any, value: any) => {
			setContentItems((prev) => {
				return prev.map((item) => {
					if (item.contentType === CONTENT_TYPE_PLAYLIST && item.id === contentId) {
						return {
							...item,
							[key]: value,
						};
					}
					return item;
				});
			});
			handleUpdatingData();
		};

		const handleUpdateSearchFilter = (contentId: any, key: any, value: any) => {
			setContentItems((prev) => {
				return prev.map((item) => {
					if (item.contentType === CONTENT_TYPE_SEARCH_FILTER && item.id === contentId) {
						return {
							...item,
							[key]: value,
						};
					}
					return item;
				});
			});
			handleUpdatingData();
		};

		const handleDuplicateContentItem = (contentId: any) => {
			const contentItem = contentItems.find((item) => item.id === contentId);
			if (!contentItem) {
				return;
			}

			setContentItems((prev) => {
				return [
					...prev,
					{
						...contentItem,
						id: generateUUID(),
					},
				];
			});
			handleUpdatingData();
		};

		const handleDeleteContentItem = (contentId: any) => {
			setContentItems((prev) => {
				return prev.filter((item) => item.id !== contentId);
			});
			handleUpdatingData();
		};

		const handleAddNewContentItem = (selectedType: string) => {
			setContentAddMenuAnchorEl(null);
			const selectedTheme = themes.find((theme) => theme.themeId === selectedThemesId);
			const {
				styling: {
					contentHeroHeader,
					contentHeroDescription,
					contentPlaylistDisplay,
					contentPlaylistRows,
					contentPlaylistShowTitle,
					contentPlaylistTitleType,
					contentHeroVideoShowTextBox,
				},
				playback: { defaultPlayerPreset },
			} = selectedTheme ?? {
				styling: {
					contentHeroHeader: '',
					contentHeroDescription: '',
					contentHeroVideoShowTextBox: true,
					contentPlaylistDisplay: PLAYLIST_DISPLAY_TYPE_GRID,
					contentPlaylistRows: 1,
					contentPlaylistShowTitle: true,
					contentPlaylistTitleType: 'normal',
				},
				playback: {
					defaultPlayerPreset: '',
				},
			};
			switch (selectedType) {
				case CONTENT_TYPE_BANNER:
					setContentItems((prev) => {
						return [
							{
								id: generateUUID(),
								contentType: CONTENT_TYPE_BANNER,
								imageUrl: '',
								enableCustomHeight: false,
								height: '',
								mobileHeight: '',
							},
							...prev,
						];
					});
					break;
				case CONTENT_TYPE_HERO_VIDEO:
					setContentItems((prev) => {
						const bannerItems = prev.filter((item) => item.contentType === CONTENT_TYPE_BANNER);
						const otherItems = prev.filter((item) => item.contentType !== CONTENT_TYPE_BANNER);
						return [
							...bannerItems,
							{
								id: generateUUID(),
								contentType: CONTENT_TYPE_HERO_VIDEO,
								mediaId: null,
								mediaTitle: '',
								mediaDescription: '',
								mediaType: '',
								contentHeroHeader: contentHeroHeader,
								contentHeroDescription: contentHeroDescription,
								isShowTextBox: contentHeroVideoShowTextBox,
								playerConfigId: defaultPlayerPreset,
								hasAttachedForm: false,
							},
							...otherItems,
						];
					});
					break;
				case CONTENT_TYPE_PLAYLIST:
					setContentItems((prev) => {
						return [
							...prev,
							{
								id: generateUUID(),
								contentType: CONTENT_TYPE_PLAYLIST,
								playlistId: '',
								displayType: contentPlaylistDisplay ?? PLAYLIST_DISPLAY_TYPE_GRID,
								rows: contentPlaylistRows ?? 1,
								isShowTitle: contentPlaylistShowTitle ?? false,
								showTitleType: contentPlaylistTitleType ?? 'normal',
							},
						];
					});
					break;
				case CONTENT_TYPE_SEARCH_FILTER:
					setContentItems((prev) => {
						return [
							...prev,
							{
								id: generateUUID(),
								contentType: CONTENT_TYPE_SEARCH_FILTER,
								data: {},
								displayType: contentPlaylistDisplay ?? PLAYLIST_DISPLAY_TYPE_GRID,
								rows: contentPlaylistRows ?? 1,
								isShowTitle: contentPlaylistShowTitle ?? false,
								showTitleType: contentPlaylistTitleType ?? 'normal',
								isOpenDefault: true,
							},
						];
					});
					break;
			}
			handleUpdatingData();
		};

		const handleAddNewFooterItem = (selectedType: string) => {
			setFooterAddMenuAnchorEl(null);
			const selectedTheme = themes.find((theme) => theme.themeId === selectedThemesId);
			const {
				styling: { footerCallToActionHeadline, footerCallToActionDescription, footerCallToActionActionUrl },
			} = selectedTheme ?? {
				styling: {
					footerCallToActionHeadline: '',
					footerCallToActionDescription: '',
					footerCallToActionActionUrl: '',
				},
			};
			switch (selectedType) {
				case FOOTER_CALL_TO_ACTION:
					setFooterItems((prev) => {
						return [
							...prev,
							{
								id: generateUUID(),
								footerType: FOOTER_CALL_TO_ACTION,
								headline: footerCallToActionHeadline,
								description: footerCallToActionDescription,
								link: footerCallToActionActionUrl,
							},
						];
					});
					break;
				case FOOTER_SOCIAL_MEDIA_LINKS:
					setFooterItems((prev) => {
						return [
							...prev,
							{
								id: generateUUID(),
								footerType: FOOTER_SOCIAL_MEDIA_LINKS,
								links: [],
							},
						];
					});
					break;
				case FOOTER_SIMPLE:
					setFooterItems((prev) => {
						return [
							...prev,
							{
								id: generateUUID(),
								footerType: FOOTER_SIMPLE,
								content: selectedTheme?.styling?.footerSimpleContent ?? '',
							},
						];
					});
					break;
			}
			handleUpdatingData();
		};

		const handleUpdateFooterItem = (footerId: any, key: any, value: any) => {
			setFooterItems((prev) => {
				return prev.map((item) => {
					if (item.id === footerId) {
						return {
							...item,
							[key]: value,
						};
					}
					return item;
				});
			});
			handleUpdatingData();
		};

		const handleDeleteFooterItem = (footerId: any) => {
			setFooterItems((prev) => {
				return prev.filter((item) => item.id !== footerId);
			});
			handleUpdatingData();
		};

		// Fetch account themes
		useEffect(() => {
			if (!defaultAccountId) {
				return;
			}

			const fetchCreatedThemes = async () => {
				const result = await fetchAccountPortalThemesService({ accountId: defaultAccountId });

				if (!result) {
					setThemes([]);
					return;
				}

				const sorted = result.sort((a, b) => a.name.localeCompare(b.name));
				themesRef.current = sorted;
				setThemes(sorted);
			};

			fetchCreatedThemes();
		}, [defaultAccountId]);

		// Fetch account playlists
		useEffect(() => {
			if (!defaultAccountId) {
				return;
			}

			const fetchCreatedPlaylists = async () => {
				const result = await loadMediasPerNavigationWithTotalCount({
					accountId: defaultAccountId,
					mediaPerPage: 200,
					offset: 0,
					mediaName: `${mediaNames.playlists}?`,
					sortType: sortTypes.newest,
					query: '',
					signal: undefined,
				});

				if (!result) {
					setPlaylists([]);
					return;
				}

				setPlaylists(result.data);
			};

			fetchCreatedPlaylists();
		}, [defaultAccountId]);

		//Reinitialize the page title when portalDetail changes
		useEffect(() => {
			const {
				content: { name, main, footer, shareThumbnail, protectedMedias },
				themeId,
			} = portalDetail;

			setPageTitle(name);
			setShareThumbnail(shareThumbnail ?? "");
			setProtectedMedias(protectedMedias ?? false);
			setSelectedThemesId(themeId);

			const contentItems = [];
			if (main) {
				const { banner, heroVideo, playlists, searchFilters } = main;

				if (banner) {
					contentItems.push({
						id: generateUUID(),
						contentType: CONTENT_TYPE_BANNER,
						imageUrl: banner.imageUrl ?? '',
						enableCustomHeight: banner.enableCustomHeight ?? false,
						height: banner.height ?? '',
						mobileHeight: banner.mobileHeight ?? '',
					});
				}

				if (heroVideo) {
					const selectedTheme = themesRef.current.find((theme) => theme.themeId === themeId);
					const {
						type,
						title,
						description,
						id,
						contentHeroHeader,
						contentHeroDescription,
						videoRatio,
						isShowTextBox,
						playerConfigId,
						hasAttachedForm,
						useAttachedForm,
						enableSimpleHeroHeading,
					} = heroVideo;
					contentItems.push({
						id: generateUUID(),
						contentType: CONTENT_TYPE_HERO_VIDEO,
						mediaType: type,
						mediaTitle: title,
						mediaDescription: description,
						mediaId: id,
						contentHeroHeader,
						contentHeroDescription,
						isShowTextBox,
						videoRatio,
						playerConfigId: playerConfigId || selectedTheme?.playback?.defaultPlayerPreset,
						hasAttachedForm,
						useAttachedForm,
						enableSimpleHeroHeading,
					});
				}

				if (playlists && playlists.length > 0) {
					const playlistItems = playlists.map((playlist: any) => {
						const { type, playlistId, enableHeadingItem, headingType, grid, order } = playlist;
						return {
							id: generateUUID(),
							contentType: CONTENT_TYPE_PLAYLIST,
							playlistId,
							displayType: type,
							rows: grid?.rows ?? 1,
							isShowTitle: enableHeadingItem ?? true,
							showTitleType: headingType ?? 'normal',
							order,
						};
					});

					contentItems.push(...playlistItems);
				}

				if (searchFilters && searchFilters.length > 0) {
					const searchFiltersItems = searchFilters.map((serachFilter: any) => {
						const { type, data, enableHeadingItem, headingType, grid, order } = serachFilter;
						return {
							id: generateUUID(),
							contentType: CONTENT_TYPE_SEARCH_FILTER,
							data,
							displayType: type,
							rows: grid?.rows ?? 1,
							isShowTitle: enableHeadingItem ?? true,
							showTitleType: headingType ?? 'normal',
							order,
						};
					});

					contentItems.push(...searchFiltersItems);
				}
			}
			setContentItems(contentItems.sort((a: any, b: any) => a.order - b.order));

			const footerItems = [];
			if (footer) {
				const { callToAction, socialMediaLinks } = footer;

				if (callToAction) {
					footerItems.push({
						id: generateUUID(),
						footerType: FOOTER_CALL_TO_ACTION,
						headline: callToAction.headline ?? '',
						description: callToAction.description ?? '',
						link: callToAction.link ?? '',
					});
				}

				if (socialMediaLinks) {
					const newLinks = socialMediaLinks.map(({ type, url }: any) => {
						return {
							id: generateUUID(),
							type,
							url,
						};
					});

					footerItems.push({
						id: generateUUID(),
						footerType: FOOTER_SOCIAL_MEDIA_LINKS,
						links: newLinks,
					});
				}

				if (footer.simpleFooter) {
					footerItems.push({
						id: generateUUID(),
						footerType: FOOTER_SIMPLE,
						content: footer.simpleFooter,
					});
				}
			}
			setFooterItems(footerItems);

			reset?.();
		}, [portalDetail]);

		const isDevelopEnvironment = envName === 'alpha' || envName === 'development';
		const isBeta = envName === 'beta';

		const portalSharingUrl = `${portalsUrl}${portalDetail.code}${isDevelopEnvironment ? '?alpha=true' : isBeta ? '?beta=true' : ''}`;
		const iframeEmbed = `<iframe src="${portalSharingUrl}" title="${
			portalDetail?.content?.name ?? 'Share your content using Qbrick Portals'
		}" />`;
		const divEmbed = `<div data-qportal-id="${portalDetail.code}"${isDevelopEnvironment ? ' data-qportal-env="alpha"' : isBeta ? ' data-qportal-env="beta"' : ''}></div>\n<script src="${qportalLoaderUrl}"></script>`;

		const selectedTheme = themes.find((theme) => theme.themeId === selectedThemesId);

		return (
			<>
				<div className={`${baseClassName}__player-container`}>
					<iframe
						id="preview-portal-id"
						src={portalSharingUrl}
						style={{ width: '100%', height: '100%', zoom: 0.5 }}
					/>
				</div>

				<div className={`${baseClassName}__media-content-container`} style={{ overflow: 'auto' }}>
					<Tabs
						value={selectedTab}
						onChange={(_e, value) => setSelectedTab(value)}
						orientation="vertical"
						className={`${baseClassName}__tab-bar`}
						TabIndicatorProps={{
							style: {
								color: '#ffffff',
								backgroundColor: 'transparent',
							},
						}}
					>
						{MediaDetailUtils.MenuPortalItems.map((item) => (
							<Tab
								key={item.value}
								value={item.value}
								icon={selectedTab === item.value ? item.activeIcon : item.icon}
								style={
									selectedTab === item.value
										? { backgroundColor: '#126CFC1A', opacity: '1' }
										: { opacity: '1' }
								}
							/>
						))}
					</Tabs>

					<div className={`${baseClassName}__media-content-container`}>
						<div className="inspect-media__detail-container">
							<div className={'inspect-media__general-info-container'}>
								{selectedTab === 'detail' && (
									<Box>
										<Box
											style={{ margin: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
											display={'flex'}
											onClick={() => setIsGeneralSectionOpen(!isGeneralSectionOpen)}
										>
											{isGeneralSectionOpen ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
											General
										</Box>
										{isGeneralSectionOpen && (
											<>
												<FormControlLabel
													className={'inspect-media__input--full'}
													style={{ display: 'flex' }}
													control={
														<Select
															value={selectedThemesId}
															variant="outlined"
															fullWidth
															onChange={(
																event: React.ChangeEvent<{
																	name?: string;
																	value: unknown;
																}>
															) => {
																const value = event.target.value as string;
																setSelectedThemesId(value);
																handleUpdatingData();
															}}
														>
															{themes.length === 0 ? (
																<MenuItem key={0} value={0}>
																	No themes available
																</MenuItem>
															) : (
																themes.map(({ name, themeId }: Theme) => {
																	return (
																		<MenuItem key={themeId} value={themeId}>
																			{name.length > 60
																				? `${name.substring(0, 60)}`
																				: name}
																		</MenuItem>
																	);
																})
															)}
														</Select>
													}
													label={
														<span className={'inspect-media__input-label'}>
															{t('INSPECT_MEDIA_MINI_PORTAL_CONFIG_CHOOSE_THEME')}
														</span>
													}
													labelPlacement={'top'}
												/>
												<FormControlLabel
													className={'inspect-media__input--full'}
													style={{ display: 'flex' }}
													control={
														<TextField
															fullWidth
															variant="outlined"
															value={pageTitle}
															placeholder={t(
																'MEDIA_LIBRARY_PLAYLIST_INSPECT_PAGE_PLAYLIST_ENTER_TITLE'
															)}
														/>
													}
													label={
														<span className={'inspect-media__input-label'}>
															{t('INSPECT_MEDIA_MINI_PORTAL_CONFIG_PAGE_TITLE')}
														</span>
													}
													onChange={(e: any) => {
														handleUpdatingData();
														setPageTitle(e.target.value);
													}}
													labelPlacement={'top'}
												/>
											</>
										)}

										<Box
											style={{ margin: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
											display={'flex'}
											onClick={() => setIsContentSectionOpen(!isContentSectionOpen)}
										>
											{isContentSectionOpen ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
											Content
										</Box>
										{isContentSectionOpen && (
											<>
												{contentItems.length > 0 && (
													<ContentListItem
														items={contentItems}
														onChange={(movedItems) => {
															const bannerContents = movedItems.filter(
																(i) => i.contentType === CONTENT_TYPE_BANNER
															);
															const heroContents = movedItems.filter(
																(i) => i.contentType === CONTENT_TYPE_HERO_VIDEO
															);
															const otherContents = movedItems.filter(
																(i) =>
																	i.contentType !== CONTENT_TYPE_BANNER &&
																	i.contentType !== CONTENT_TYPE_HERO_VIDEO
															);

															setContentItems([
																...bannerContents,
																...heroContents,
																...otherContents,
															]);
															handleUpdatingData();
														}}
														renderItem={(item, expandedId, setExpandedId) => (
															<ContentItem
																id={item.id}
																item={item}
																playlists={playlists}
																expandedId={expandedId ?? ''}
																miniMediaLibraryRef={miniMediaLibraryRef}
																accountPreset={accountPreset}
																handleClearSelectedHeroMedia={
																	handleClearSelectedHeroMedia
																}
																handleUpdateHeroMedia={handleUpdateHeroMedia}
																handleUpdateBanner={handleUpdateBanner}
																handleUpdatePlaylist={handleUpdatePlaylist}
																handleUpdateSearchFilter={handleUpdateSearchFilter}
																handleDeleteContentItem={handleDeleteContentItem}
																handleDuplicateContentItem={handleDuplicateContentItem}
																setExpandedId={setExpandedId}
															/>
														)}
													/>
												)}
												<Button
													variant="contained"
													color="primary"
													style={{ margin: '10px 0', padding: 5, minWidth: 0 }}
													onClick={(e) => {
														setContentAddMenuAnchorEl(e.currentTarget);
													}}
												>
													<AddIcon fontSize={'large'} />
												</Button>
											</>
										)}

										<Menu
											anchorEl={contentAddMenuAnchorEl}
											onClose={() => {
												setContentAddMenuAnchorEl(null);
											}}
											open={Boolean(contentAddMenuAnchorEl)}
											anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
										>
											{contentAddMenuItems.map(({ id, label, icon }) => {
												return (
													<MenuItem
														key={id}
														style={{ display: 'flex', alignItems: 'center' }}
														onClick={() => {
															handleAddNewContentItem(id);
														}}
														disabled={
															(id === CONTENT_TYPE_HERO_VIDEO ||
																id === CONTENT_TYPE_BANNER) &&
															!!contentItems.find((item) => item.contentType === id)
														}
													>
														{icon}
														<div style={{ marginLeft: 5 }}>{t(label)}</div>
													</MenuItem>
												);
											})}
										</Menu>

										<Box
											style={{ margin: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
											display={'flex'}
											onClick={() => setIsFooterSectionOpen(!isFooterSectionOpen)}
										>
											{isFooterSectionOpen ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
											Footer
										</Box>

										{isFooterSectionOpen && (
											<>
												{footerItems.length > 0 && (
													<FooterListItem
														items={footerItems}
														selectedTheme={selectedTheme}
														handleUpdateFooterItem={handleUpdateFooterItem}
														handleDeleteFooterItem={handleDeleteFooterItem}
													/>
												)}
												<Button
													variant="contained"
													color="primary"
													style={{ margin: '10px 0', padding: 5, minWidth: 0 }}
													onClick={(e) => {
														setFooterAddMenuAnchorEl(e.currentTarget);
													}}
												>
													<AddIcon fontSize={'large'} />
												</Button>
											</>
										)}

										<Menu
											anchorEl={footerAddMenuAnchorEl}
											onClose={() => {
												setFooterAddMenuAnchorEl(null);
											}}
											open={Boolean(footerAddMenuAnchorEl)}
											anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
										>
											{footerAddMenuItems.map(({ id, label, icon, enabledInThemeKey }) => {
												return (
													<MenuItem
														key={id}
														style={{ display: 'flex', alignItems: 'center' }}
														onClick={() => {
															handleAddNewFooterItem(id);
														}}
														disabled={
															!!footerItems.find((item) => item.footerType === id) ||
															(selectedTheme && !selectedTheme.styling[enabledInThemeKey])
														}
													>
														{icon}
														<div style={{ marginLeft: 5 }}>{t(label)}</div>
													</MenuItem>
												);
											})}
										</Menu>
									</Box>
								)}

								{selectedTab === 'share' && (
									<Box>
										<FormControlLabel
											className={'inspect-media__input--full'}
											style={{ display: 'flex' }}
											control={
												<Box
													display="flex"
													flexDirection="row"
													style={{ width: '100%', marginTop: 0 }}
													justifyContent="center"
													alignItems="center"
												>
													<Box style={{ flex: 1, margin: 0 }}>
														<TextField
															fullWidth
															variant="outlined"
															value={portalSharingUrl}
															InputProps={{
																readOnly: true,
															}}
														/>
													</Box>

													<CopyToClipboard text={portalSharingUrl}>
														<IconButton>
															<FileCopyIcon />
														</IconButton>
													</CopyToClipboard>
												</Box>
											}
											label={
												<span className={'inspect-media__input-label'}>
													{t('INSPECT_MEDIA_MINI_PORTAL_CONFIG_SHARE_LINK')}
												</span>
											}
											labelPlacement={'top'}
										/>
										<Box display="flex" alignItems="center" style={{ margin: '0px' }}>
											<SubdirectoryArrowRightRoundedIcon style={{
												fill: "#867f7f",
												width: "32px",
												marginTop: "8px"
											 }} />
											<FormControlLabel
												className={'inspect-media__input--full'}
												style={{ display: 'flex', flex: 1, marginTop: "0px" }}
												control={
													<Box
														display="flex"
														flexDirection="row"
														style={{ width: '100%', marginTop: 0 }}
														justifyContent="center"
														alignItems="center"
													>
														<Box style={{ flex: 1, margin: 0 }}>
															<TextField
																fullWidth
																variant="outlined"
																value={shareThumbnail}
																placeholder={t('COMMON_IMAGE_URL')}
															/>
														</Box>

														<div style={{ width: '42px' }} />
													</Box>
												}
												label={
													<span className={'inspect-media__input-label'}>
														{t('INSPECT_MEDIA_MINI_PORTAL_CONFIG_SHARE_THUMBNAIL')}
													</span>
												}
												onChange={(e: any) => {
													handleUpdatingData();
													setShareThumbnail(e.target.value);
													setIsThumbnailChanged(true);
												}}
												labelPlacement={'top'}
											/>
										</Box>
										<div style={{ margin: '20px 0px 30px 0px', background: 'rgba(0, 0, 0, 0.20)', height: '1px' }} />

										<FormControlLabel
											className={'inspect-media__input--full'}
											style={{ display: 'flex' }}
											control={
												<Box
													display="flex"
													flexDirection="row"
													style={{ width: '100%', marginTop: 0 }}
													justifyContent="center"
													alignItems="center"
												>
													<Box style={{ flex: 1, margin: 0 }}>
														<TextField
															fullWidth
															variant="outlined"
															value={divEmbed}
															InputProps={{
																readOnly: true,
															}}
														/>
													</Box>

													<CopyToClipboard text={divEmbed}>
														<IconButton>
															<FileCopyIcon />
														</IconButton>
													</CopyToClipboard>
												</Box>
											}
											label={
												<span className={'inspect-media__input-label'}>
													{t(
														'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_PLAYER_INFO_DIV_EMBED'
													)}
												</span>
											}
											labelPlacement={'top'}
										/>
										<FormControlLabel
											className={'inspect-media__input--full'}
											style={{ display: 'flex' }}
											control={
												<Box
													display="flex"
													flexDirection="row"
													style={{ width: '100%', marginTop: 0 }}
													justifyContent="center"
													alignItems="center"
												>
													<Box style={{ flex: 1, margin: 0 }}>
														<TextField
															fullWidth
															variant="outlined"
															value={iframeEmbed}
															InputProps={{
																readOnly: true,
															}}
														/>
													</Box>

													<CopyToClipboard text={iframeEmbed}>
														<IconButton>
															<FileCopyIcon />
														</IconButton>
													</CopyToClipboard>
												</Box>
											}
											label={
												<span className={'inspect-media__input-label'}>
													{t(
														'MEDIA_LIBRARY_MEDIA_INSPECT_PAGE_DETAILS_PLAYER_INFO_IFRAME_EMBED'
													)}
												</span>
											}
											labelPlacement={'top'}
										/>
									</Box>
								)}

								{selectedTab === 'restrict-access' && (
									<Box>
										<div
											style={{ fontSize: 12, fontWeight: 600, margin: "0", padding: "0" }}
											className={'inspect-media__input-label no-padding'}
										>
											{t('INSPECT_MEDIA_MINI_PORTAL_CONFIG_RESTRICT_ACCESS')}
										</div>
										<FormControlLabel
											className={'inspect-media__input--full no-padding'}
											style={{ display: 'flex', margin: "0px 0px 10px 0px" }}
											control={
												<Checkbox
													style={{ color: protectedMedias ? '#126CFC' : '#000000', marginLeft: "-8px" }}
													checked={protectedMedias}
													classes={{ root: customStyles.tickSize }}
												/>
											}
											label={
												<span className={'inspect-media__input-label no-padding'} style={{ fontWeight: 'normal' }}>
													{t('INSPECT_MEDIA_MINI_PORTAL_CONFIG_RESTRICT_ACCESS_LABEL')}
												</span>
											}
											onChange={(_e: any, checked: boolean) => {
												setProtectedMedias(checked);
												handleUpdatingData();
											}}
										/>
										<div
											style={{
												backgroundColor: "rgba(18, 108, 252, 0.10)",
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-start",
												gap: 10,
												padding: 10,
												borderRadius: 4,
												fontSize: 12,
												fontWeight: 'normal'
											}}
										>
											<div><b>{t('PORTAL_PASSWORD_HELP_TITLE')}</b></div>
											<div>
												{t('PORTAL_PASSWORD_HELP_DESCRIPTION')}

												<div style={{ marginTop: "12px" }}>
													<b>{t('PORTAL_PASSWORD_HELP_DESCRIPTION_2')}</b>
												</div>

												<ol style={{ paddingLeft: "20px" }}>
													<li>{t('PORTAL_PASSWORD_HELP_DESCRIPTION_3')}</li>
													<li>{t('PORTAL_PASSWORD_HELP_DESCRIPTION_4')}</li>
												</ol>

												{t('PORTAL_PASSWORD_HELP_DESCRIPTION_5')}
											</div>

											<Link
												href="https://support.qbrick.com/hc/en-us/articles/4719739613853-Media-access-restrictions"
												target="_blank"
												style={{ color: "#126CFC", display: 'block' }}
											>
												{t('INSPECT_MEDIA_MINI_PORTAL_CONFIG_RESTRICT_ACCESS_LEARN_MORE')}
											</Link>
										</div>
									</Box>
								)}
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}
);

export default PortalContent;